import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import { Button, Card, Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { TableSecondaryText } from 'styles';

import { TableTimestamp } from 'components';
import AuditLog from 'components/AuditLog';
import RenderInGrid from 'components/Layout/RenderInGrid';
import { GridContainer, Tag } from 'components/Layout/common';
import { ImageItem } from 'components/Layout/list';
import Label from 'components/Text/Label';
import { ActionsBtnGroup, SectionHeader } from 'containers/BDO/utils';
import { IParamsWithContainId } from 'utils/types';

import BDOSettingAddEditModal from '../BDOSettingAddEditModal';
import { getBDOSetting } from '../action';
import { IBDOSetting, IBDOSettingsState } from '../types';

const HotelShow = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const params = useParams<IParamsWithContainId>();

  const hotelId = params.id;

  const dispatch = useDispatch();
  const {
    bdoSetting,
    loading,
    error
  }: { bdoSetting: IBDOSetting | null; loading: boolean; error: any } = useSelector(
    (state: { bdoSettings: IBDOSettingsState }) => state.bdoSettings
  );

  useEffect(() => {
    if (hotelId) {
      dispatch(getBDOSetting(hotelId));
    }
  }, [hotelId, dispatch]);

  const renderInGrid = (description, content, hasContentChanged?: boolean) => {
    return (
      <RenderInGrid
        description={description}
        content={content}
        hasContentChanged={hasContentChanged}
      />
    );
  };

  const renderTags = (tags = []) => {
    if (isEmpty(tags)) {
      if (!tags.length) {
        return <Label>N.A</Label>;
      }
    }
    if (!isEmpty(tags)) {
      return tags.map(tag => {
        return tag?.id ? (
          <Tag key={tag.id}>
            <Label nomargin>{tag.name}</Label>
          </Tag>
        ) : null;
      });
    } else {
      return <Label>N.A</Label>;
    }
  };

  const handleEditClick = () => {
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDuplicateClick = () => {
    setIsEdit(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (isEdit) {
      dispatch(getBDOSetting(hotelId));
    }
  };

  return (
    <>
      <Card>
        <Row justify="space-between">
          <SectionHeader>Hotel Loyalty Program Details</SectionHeader>
          <ActionsBtnGroup>
            <Button size="large" type="primary" ghost onClick={handleDuplicateClick}>
              Duplicate
            </Button>
            <Button size="large" type="primary" icon={<EditOutlined />} onClick={handleEditClick}>
              Edit
            </Button>
          </ActionsBtnGroup>
        </Row>
        {renderInGrid('Name:', <Label dark>{bdoSetting?.name}</Label>)}
        {renderInGrid('Description:', <Label dark>{bdoSetting?.description}</Label>)}
        {renderInGrid(
          'Loyalty Acct Currency:',
          <Label dark>{bdoSetting?.loyalty_points_name || '-'}</Label>
        )}
        {renderInGrid(
          'Loyalty Acct No Placeholder:',
          <Label dark>{bdoSetting?.loyalty_acct_placeholder || '-'}</Label>
        )}
        {renderInGrid(
          'Logo:',
          <ImageItem>
            <img src={bdoSetting?.image?.url} alt={bdoSetting?.name} />
          </ImageItem>
        )}
        {renderInGrid(
          'Conversion Rate:',
          <Label dark>
            {bdoSetting?.conversion_rate} Hotel Loyalty points = 1 Credit Card Point
          </Label>
        )}
        {renderInGrid(
          'Min. Redeem Amt.:',
          <Label dark>{Math.floor(bdoSetting?.min_redeem)} points</Label>
        )}
        {renderInGrid(
          'Pts Redeem Increment:',
          <Label dark>{bdoSetting?.increment_amount} points</Label>
        )}
        {renderInGrid('LRS Reward Code:', <Label dark>{bdoSetting?.lrs_reward_code}</Label>)}
        {renderInGrid('Applicable Credit Cards:', renderTags(bdoSetting?.cards))}
        {renderInGrid(
          'Start & End Date:',
          <Label dark>
            <Col>
              <GridContainer columns="auto 10px auto" gap="10px" inline>
                {bdoSetting?.start_date ? (
                  <TableTimestamp timestamp={bdoSetting?.start_date} />
                ) : (
                  'N/A'
                )}
                <TableSecondaryText>~</TableSecondaryText>
                {bdoSetting?.end_date ? <TableTimestamp timestamp={bdoSetting?.end_date} /> : 'N/A'}
              </GridContainer>
            </Col>
          </Label>
        )}
      </Card>
      <AuditLog
        entity="bdo_transfer_request_card_item"
        id={hotelId}
        isChanges2
        key={bdoSetting?.id}
      />
      <BDOSettingAddEditModal
        modalOpen={isModalOpen}
        closeModal={handleCloseModal}
        title={'Hotel Loyalty Program Details'}
        BDOSettingType={'Hotel'}
        cardItemType={'hotel_loyalty'}
        isEdit={isEdit}
        selectedBDOSetting={bdoSetting?.id}
      />
    </>
  );
};

export default HotelShow;

HotelShow.propTypes = {
  match: PropTypes.object
};
